import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

interface Location {
  _id: string;
  name: string;
  description: string;
  images: string[];
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [featuredLocations, setFeaturedLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFeaturedLocations = async () => {
      try {
        const response = await fetch(`${API_URL}/api/locations/featured`);
        if (!response.ok) {
          throw new Error('Failed to fetch featured locations');
        }
        const data = await response.json();
        setFeaturedLocations(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedLocations();
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '60vh',
          backgroundImage: 'url(https://images.unsplash.com/photo-1502680390469-be75c86b636f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            component="h1"
            color="white"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Discover Delaware's
            <br />
            Kayaking Paradise
          </Typography>
          <Typography variant="h5" color="white" paragraph>
            Explore the First State's most beautiful waterways
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/locations')}
              sx={{ mt: 2 }}
            >
              Explore Locations
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/map')}
              sx={{ mt: 2, color: 'white', borderColor: 'white' }}
            >
              View Map
            </Button>
          </Box>
        </Container>
      </Box>

      {/* About Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom>
              Why Kayak in Delaware?
            </Typography>
            <Typography paragraph>
              Delaware offers a diverse range of kayaking experiences, from peaceful
              creeks and rivers to the unique ecosystem of the Delaware Bay. With
              over 1,000 miles of waterways, the First State provides endless
              opportunities for both beginners and experienced kayakers.
            </Typography>
            <Typography paragraph>
              Our state's waterways are home to rich wildlife, including bald
              eagles, ospreys, and various waterfowl. The calm waters of our
              rivers and creeks make Delaware an ideal destination for family
              kayaking adventures.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                backgroundColor: 'primary.light',
                color: 'white',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Best Time to Kayak
              </Typography>
              <Typography paragraph>
                Spring and fall offer the most comfortable temperatures for
                kayaking in Delaware. Summer provides longer days and warmer
                water, while winter kayaking can be a unique experience for the
                adventurous.
              </Typography>
              <Typography variant="h6" gutterBottom>
                What to Bring
              </Typography>
              <Typography>
                • Personal flotation device (PFD)
                <br />
                • Water and snacks
                <br />
                • Sun protection
                <br />
                • Waterproof bag for essentials
                <br />
                • Appropriate clothing for the weather
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Featured Locations */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" gutterBottom align="center">
            Featured Locations
          </Typography>
          {loading ? (
            <Typography align="center">Loading featured locations...</Typography>
          ) : error ? (
            <Typography align="center" color="error">{error}</Typography>
          ) : (
            <Grid container spacing={4} sx={{ mt: 2 }}>
              {featuredLocations.map((location) => (
                <Grid item key={location._id} xs={12} md={4}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => navigate(`/locations/${location._id}`)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={location.images[0] || 'https://images.unsplash.com/photo-1502680390469-be75c86b636f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80'}
                      alt={location.name}
                    />
                    <CardContent>
                      <Typography gutterBottom variant="h5" component="h3">
                        {location.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {location.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/locations')}
            >
              View All Locations
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'primary.main',
            color: 'white',
          }}
        >
          <Typography variant="h4" gutterBottom>
            Ready to Start Your Adventure?
          </Typography>
          <Typography paragraph>
            Explore our comprehensive guide to Delaware's kayaking locations,
            complete with detailed information about difficulty levels, amenities,
            and user reviews.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/locations')}
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
            >
              Find Your Next Kayaking Spot
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/map')}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              View Interactive Map
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Home; 